import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { Sparkles, Clock, Gauge, ArrowRight, Check, Pencil, X } from "lucide-react";
import { get } from "@/lib/api";
import { formatDate } from "@/lib/data";

export default function Pricing() {
  const { data: suggestions = [] } = useQuery({ queryKey: ["pricing-suggestions"], queryFn: () => get("/partner/pricing-suggestions") });
  const [handled, setHandled] = useState({});
  const [editing, setEditing] = useState(null);
  const [editValue, setEditValue] = useState("");

  const act = (id, type, msg) => { setHandled((h) => ({ ...h, [id]: type })); toast.success(msg); setEditing(null); };
  const visible = suggestions.filter((s) => !handled[s.session_id]);

  const btn = "flex flex-1 items-center justify-center gap-1.5 rounded-md border px-3 py-2.5 text-sm font-medium transition-colors";

  return (
    <div>
      <div className="mb-6 flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-[#2D2926] text-white"><Sparkles className="h-5 w-5" /></div>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Akıllı Fiyatlandırma</h1>
          <p className="text-sm text-[#64748B]">Doluluk ve zamana göre otomatik fiyat önerileri (demo)</p>
        </div>
      </div>

      {visible.length === 0 ? (
        <div className="rounded-md border border-dashed border-[#E2E8F0] bg-white p-12 text-center text-[#64748B]">
          Şu an için tüm öneriler değerlendirildi. Yeni öneriler seans saatleri yaklaştıkça oluşacaktır.
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {visible.map((s) => (
            <div key={s.session_id} data-testid={`pricing-suggestion-${s.session_id}`} className="rounded-md border border-[#E2E8F0] bg-white p-5">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold">{s.title}</h3>
                <span className="text-xs text-[#64748B]">{formatDate(s.date)} · {s.start_time}</span>
              </div>
              <div className="mt-3 flex gap-4 text-sm text-[#475569]">
                <span className="flex items-center gap-1.5"><Clock className="h-4 w-4 text-[#94A3B8]" /> {s.hours_until} saat kaldı</span>
                <span className="flex items-center gap-1.5"><Gauge className="h-4 w-4 text-[#94A3B8]" /> %{s.occupancy} doluluk</span>
              </div>

              <div className="mt-4 flex items-center gap-3 rounded-md bg-[#F1F5F9] p-4">
                <div className="text-center">
                  <p className="text-xs text-[#94A3B8]">Mevcut</p>
                  <p className="text-xl font-semibold text-[#94A3B8] line-through">{s.current_price}</p>
                </div>
                <ArrowRight className="h-4 w-4 text-[#64748B]" />
                <div className="text-center">
                  <p className="text-xs text-[#64748B]">Önerilen</p>
                  <p className="text-2xl font-bold text-[#2D2926]">{s.suggested_price}</p>
                </div>
                <p className="ml-2 text-xs leading-snug text-[#475569]">{s.reason}</p>
              </div>

              {editing === s.session_id ? (
                <div className="mt-4 flex gap-2">
                  <input data-testid={`pricing-edit-input-${s.session_id}`} type="number" value={editValue} onChange={(e) => setEditValue(e.target.value)} className="w-24 rounded-md border border-[#E2E8F0] px-3 py-2 text-sm outline-none focus:border-[#2D2926]" />
                  <button data-testid={`pricing-save-${s.session_id}`} onClick={() => act(s.session_id, "edited", `Fiyat ${editValue} krediye güncellendi`)} className="rounded-md bg-[#2D2926] px-4 py-2 text-sm font-medium text-white">Kaydet</button>
                  <button onClick={() => setEditing(null)} className="rounded-md border border-[#E2E8F0] px-4 py-2 text-sm">Vazgeç</button>
                </div>
              ) : (
                <div className="mt-4 flex gap-2">
                  <button data-testid={`pricing-approve-${s.session_id}`} onClick={() => act(s.session_id, "approved", "Öneri onaylandı, fiyat güncellendi")} className={`${btn} border-[#E2E8F0] bg-white text-[#2D2926] hover:bg-[#F1F5F9]`}><Check className="h-4 w-4" /> Onayla</button>
                  <button data-testid={`pricing-change-${s.session_id}`} onClick={() => { setEditing(s.session_id); setEditValue(String(s.suggested_price)); }} className={`${btn} border-[#E2E8F0] bg-white text-[#2D2926] hover:bg-[#F1F5F9]`}><Pencil className="h-4 w-4" /> Değiştir</button>
                  <button data-testid={`pricing-ignore-${s.session_id}`} onClick={() => act(s.session_id, "ignored", "Öneri yoksayıldı")} className={`${btn} border-[#E2E8F0] bg-white text-[#2D2926] hover:bg-[#F1F5F9]`}><X className="h-4 w-4" /> Yoksay</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
